import { useState, useEffect } from 'react';
import './App.css';
import { useGame, useScenarioLoader } from './systems';
import { StorySelectionScreen } from './components/story-selection-screen';
import { LoadingScreen, ErrorScreen, GameplayScreen, AdminOverlayEditor, LandingPage } from './components';
import { Spinner } from './components/spinner';
import { ScenarioStudio } from './components/scenario-editor/ScenarioStudio';
import Login from './components/auth/Login';
import OidcCallback from './components/auth/OidcCallback';
import RegisterUser from './components/auth/RegisterUser';
import DepartmentSetup from './components/auth/DepartmentSetup';
import DarwinXDashboard from './components/admin/DarwinXDashboard';
import AnalyticsDashboard from './components/admin/AnalyticsDashboard';
import ReviewsDashboard from './components/admin/ReviewsDashboard';
import UserProfile from './components/auth/UserProfile';
import { LeaderboardScreen } from './components/leaderboard/leaderboard-screen';
import { ProfileSetupScreen } from './components/leaderboard/profile-setup-screen';
import { Navigation } from './components/navigation';
import { useAuth } from './systems/auth-context';
import { useWorkspace } from './systems/workspace-context';
import { isSsoOnlyHostname } from './systems/sso-only-host';
import {
  canAccessAnalytics,
  canAccessMasterDashboard,
  canAccessScenarioTools,
  canAccessReviewsDashboard,
} from './systems/roles';
import { Scenario } from './types';
import { parseLibraryItemId } from './systems/library-item';
import { refreshLocalCompletionsFromServer } from './systems/mission-progress';
import { usePostHogScreen } from './systems/posthog-screen';
import { getScormLaunch, markScormAutoStarted, reportScormError, reportScormReady } from './systems/scorm-bridge';
import type { ScenarioAssetLayer } from './systems/scenario-asset-url';
import { needsPlayerProfile, useLeaderboardEnabled } from './systems/use-leaderboard-enabled';
import { syncLocalCampaignResults } from './systems/mission-results';

type AppScreen = 'landing' | 'selection' | 'loading' | 'error' | 'gameplay' | 'admin' | 'scenarioStudio' | 'login' | 'register' | 'oidcCallback' | 'dashboard' | 'enterpriseDashboard' | 'analytics' | 'reviews' | 'leaderboard';

const MIN_GAMEPLAY_WIDTH = 1100;
const MIN_GAMEPLAY_HEIGHT = 600;

function isViewportTooSmall(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.innerWidth < MIN_GAMEPLAY_WIDTH || window.innerHeight < MIN_GAMEPLAY_HEIGHT;
}

function App() {
  const { user, isLoading: authLoading, sessionNotice } = useAuth();
  const { isWorkspaceReady } = useWorkspace();
  const leaderboardEnabled = useLeaderboardEnabled();
  const ssoOnly = isSsoOnlyHostname(window.location.hostname);
  const game = useGame();
  const [currentScreen, setCurrentScreen] = useState<AppScreen>('landing');
  const [currentScenario, setCurrentScenario] = useState<Scenario | null>(null);
  const [currentScenarioId, setCurrentScenarioId] = useState<string | null>(null);
  const [currentMissionId, setCurrentMissionId] = useState<string | null>(null);
  const [lastAttemptedScenarioId, setLastAttemptedScenarioId] = useState<string | null>(null);
  const [lastAttemptedMissionId, setLastAttemptedMissionId] = useState<string | null>(null);
  const [lastAttemptedLayer, setLastAttemptedLayer] = useState<ScenarioAssetLayer>('master');
  const [lastAttemptedEditorCatalog, setLastAttemptedEditorCatalog] = useState(false);
  const [currentPlayLayer, setCurrentPlayLayer] = useState<ScenarioAssetLayer>('master');
  const [libraryRefreshKey, setLibraryRefreshKey] = useState(0);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isPlayerProfileEditOpen, setIsPlayerProfileEditOpen] = useState(false);
  const [isViewportTooSmallForGameplay, setIsViewportTooSmallForGameplay] = useState<boolean>(() => isViewportTooSmall());

  useEffect(() => {
    if (window.location.pathname === '/auth/callback') {
      setCurrentScreen('oidcCallback');
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsViewportTooSmallForGameplay(isViewportTooSmall());
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Read URL parameters on mount to restore position
  useEffect(() => {
    if (authLoading || !user) return;
    if (currentScreen !== 'selection') return; // Only check URL when on selection screen

    const urlParams = new URLSearchParams(window.location.search);
    const scenarioId = urlParams.get('scenario');
    const missionId = urlParams.get('mission');
    const sceneId = urlParams.get('scene');
    const step = urlParams.get('step');
    const layerParam = urlParams.get('layer');
    const layer: ScenarioAssetLayer = layerParam === 'draft' ? 'draft' : 'master';
    const editorCatalogMode = layerParam === 'draft' && canAccessScenarioTools(user);

    if (scenarioId && sceneId && step !== null) {
      setLastAttemptedScenarioId(scenarioId);
      setLastAttemptedMissionId(missionId);
      setLastAttemptedLayer(layer);
      setLastAttemptedEditorCatalog(editorCatalogMode);
      loadScenario(scenarioId, missionId ?? undefined, layer, editorCatalogMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, currentScreen]);

  // Redirect users without the right role away from admin screens
  useEffect(() => {
    if (authLoading || !user) return;

    const screenAllowed: Record<AppScreen, boolean> = {
      landing: true,
      login: true,
      register: true,
      oidcCallback: true,
      selection: true,
      loading: true,
      error: true,
      gameplay: true,
      dashboard: canAccessAnalytics(user) || canAccessMasterDashboard(user),
      enterpriseDashboard: canAccessMasterDashboard(user),
      analytics: canAccessAnalytics(user),
      admin: canAccessScenarioTools(user),
      scenarioStudio: canAccessScenarioTools(user),
      reviews: canAccessReviewsDashboard(user),
      leaderboard: leaderboardEnabled,
    };

    if (!screenAllowed[currentScreen]) {
      setCurrentScreen('selection');
    }
  }, [user, authLoading, currentScreen, leaderboardEnabled]);

  // Boucliers et alliés gagnés avant le classement : repris des sauvegardes du navigateur.
  useEffect(() => {
    if (user && currentScreen === 'selection') {
      syncLocalCampaignResults(user.id);
    }
  }, [user, currentScreen]);

  // Handle protected routes and initial state
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      if (ssoOnly && currentScreen === 'register') {
        setCurrentScreen('login');
      } else if (sessionNotice) {
        if (currentScreen !== 'login') {
          setCurrentScreen('login');
        }
      } else if (currentScreen !== 'login' && currentScreen !== 'register' && currentScreen !== 'oidcCallback') {
        setCurrentScreen('landing');
      }
    } else if (currentScreen === 'landing' || currentScreen === 'login' || currentScreen === 'register' || currentScreen === 'oidcCallback') {
      setCurrentScreen('selection');
    }
  }, [user, authLoading, currentScreen, sessionNotice, ssoOnly]);

  const { loadScenario, loadingState } = useScenarioLoader({
    onLoadStart: () => {
      setCurrentScreen('loading');
    },
    onLoadComplete: (scenario: Scenario, scenarioId: string, missionId?: string, layer: ScenarioAssetLayer = 'master') => {
      reportScormReady(scenarioId);
      setCurrentScenario(scenario);
      setCurrentScenarioId(scenarioId);
      setCurrentMissionId(missionId ?? null);
      setCurrentPlayLayer(layer);
      setCurrentScreen('gameplay');
      setLastAttemptedScenarioId(null);
      setLastAttemptedMissionId(null);
      setLastAttemptedLayer('master');
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('scenario', scenarioId);
      if (missionId) {
        urlParams.set('mission', missionId);
      } else {
        urlParams.delete('mission');
      }
      if (layer === 'draft') {
        urlParams.set('layer', 'draft');
      } else {
        urlParams.delete('layer');
      }
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({}, '', newUrl);
    },
    onLoadError: (error: Error, scenarioId: string) => {
      reportScormError(scenarioId, error.message);
      setCurrentScreen('error');
    },
  });

  // Opened from a SCORM package: start its scenario once the player is logged in.
  useEffect(() => {
    if (authLoading || !user || !user.department || !isWorkspaceReady) return;
    if (needsPlayerProfile(user)) return;
    if (currentScreen !== 'selection') return;

    const launch = getScormLaunch();
    if (!launch || launch.autoStarted) return;
    const urlParams = new URLSearchParams(window.location.search);
    // The URL restore above already reopens the scenario at the saved position.
    if (urlParams.get('scene') && urlParams.get('step') !== null) return;

    markScormAutoStarted();
    handleSelectStory(launch.scenarioId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, currentScreen, isWorkspaceReady]);

  const handleSelectStory = (
    libraryItemId: string,
    explicitMissionId?: string,
    layer: ScenarioAssetLayer = 'master',
    editorCatalogMode = false,
  ) => {
    const { scenarioId, missionId: parsedMissionId } = parseLibraryItemId(libraryItemId);
    const missionId = explicitMissionId ?? parsedMissionId ?? null;
    game.resetGame();
    setCurrentMissionId(missionId);
    setCurrentPlayLayer(layer);
    setLastAttemptedScenarioId(scenarioId);
    setLastAttemptedMissionId(missionId);
    setLastAttemptedLayer(layer);
    setLastAttemptedEditorCatalog(editorCatalogMode);
    loadScenario(scenarioId, missionId ?? undefined, layer, editorCatalogMode);
  };

  const handleBackToSelection = async () => {
    await refreshLocalCompletionsFromServer();
    game.resetGame();
    setCurrentScreen('selection');
    setCurrentScenario(null);
    setCurrentScenarioId(null);
    setCurrentMissionId(null);
    setLastAttemptedScenarioId(null);
    setLastAttemptedMissionId(null);
    setLastAttemptedLayer('master');
    setLastAttemptedEditorCatalog(false);
    setCurrentPlayLayer('master');
    setLibraryRefreshKey((key) => key + 1);
    window.history.replaceState({}, '', window.location.pathname);
  };

  const showPlayerProfileGate = currentScreen !== 'gameplay' && needsPlayerProfile(user);
  const trackedScreen =
    user && !user.department
      ? 'departmentSetup'
      : showPlayerProfileGate
        ? 'profileSetup'
        : isProfileOpen
          ? 'profile'
          : currentScreen;

  usePostHogScreen(trackedScreen);

  const handleRetry = () => {
    if (lastAttemptedScenarioId) {
      setCurrentPlayLayer(lastAttemptedLayer);
      loadScenario(
        lastAttemptedScenarioId,
        lastAttemptedMissionId ?? undefined,
        lastAttemptedLayer,
        lastAttemptedEditorCatalog
      );
    } else {
      handleBackToSelection();
    }
  };

  if (authLoading) {
    return <Spinner layout="fullscreen" label="Vérification de la session…" />;
  }

  if (user && !user.department) {
    return (
      <div className="app">
        <DepartmentSetup onComplete={() => setCurrentScreen('selection')} />
      </div>
    );
  }

  // Workspace avec classement : le joueur choisit son pseudo avant d'entrer.
  if (showPlayerProfileGate) {
    return (
      <div className="app">
        <ProfileSetupScreen mode="first" onDone={() => setCurrentScreen('selection')} />
      </div>
    );
  }

  return (
    <div className={`app ${user && currentScreen !== 'gameplay' ? 'has-nav' : ''}`}>
      {user && currentScreen !== 'gameplay' && (
        <Navigation 
          currentScreen={currentScreen}
          onScreenChange={setCurrentScreen}
          onOpenProfile={() => setIsProfileOpen(true)}
        />
      )}

      {isProfileOpen && (
        <UserProfile 
          isOpen={isProfileOpen} 
          onClose={() => setIsProfileOpen(false)} 
          onEditPlayerProfile={() => {
            setIsProfileOpen(false);
            setIsPlayerProfileEditOpen(true);
          }}
        />
      )}

      {isPlayerProfileEditOpen && user?.leaderboard_enabled && (
        <ProfileSetupScreen
          mode="edit"
          onDone={() => setIsPlayerProfileEditOpen(false)}
          onCancel={() => setIsPlayerProfileEditOpen(false)}
        />
      )}

      {currentScreen === 'landing' && (
        <LandingPage 
          onLogin={() => setCurrentScreen('login')}
          onRegister={ssoOnly ? undefined : () => setCurrentScreen('register')}
        />
      )}

      {currentScreen === 'login' && (
        <Login onSwitchToRegisterUser={ssoOnly ? undefined : () => setCurrentScreen('register')} />
      )}

      {currentScreen === 'register' && !ssoOnly && (
        <RegisterUser onSwitchToLogin={() => setCurrentScreen('login')} />
      )}

      {currentScreen === 'oidcCallback' && (
        <OidcCallback onComplete={() => setCurrentScreen('login')} />
      )}

      {currentScreen === 'dashboard' && (canAccessAnalytics(user) || canAccessMasterDashboard(user)) && (
        canAccessMasterDashboard(user) ? (
          <DarwinXDashboard />
        ) : (
          <AnalyticsDashboard />
        )
      )}

      {currentScreen === 'enterpriseDashboard' && canAccessMasterDashboard(user) && (
        <AnalyticsDashboard />
      )}

      {currentScreen === 'analytics' && canAccessAnalytics(user) && (
        <AnalyticsDashboard />
      )}

      {currentScreen === 'selection' && (
        <div className="selection-container">
          <StorySelectionScreen 
            onSelectStory={handleSelectStory}
            refreshKey={libraryRefreshKey}
            onOpenLeaderboard={leaderboardEnabled ? () => setCurrentScreen('leaderboard') : undefined}
          />
        </div>
      )}
      
      {currentScreen === 'admin' && canAccessScenarioTools(user) && (
        <AdminOverlayEditor 
          onBack={handleBackToSelection}
        />
      )}

      {currentScreen === 'scenarioStudio' && canAccessScenarioTools(user) && (
        <ScenarioStudio onBack={handleBackToSelection} />
      )}

      {currentScreen === 'leaderboard' && leaderboardEnabled && <LeaderboardScreen />}

      {currentScreen === 'reviews' && canAccessReviewsDashboard(user) && (
        <ReviewsDashboard onBack={handleBackToSelection} />
      )}
      
      {currentScreen === 'loading' && (
        <LoadingScreen 
          progress={loadingState.progress}
          message="Loading assets..."
        />
      )}
      
      {currentScreen === 'error' && (
        <ErrorScreen
          title={loadingState.error?.message === 'Invalid scenario format' ? 'Validation Error' : 'Failed to Load Story'}
          message={loadingState.error?.message || 'An error occurred while loading the story'}
          details={loadingState.errorDetails}
          onRetry={lastAttemptedScenarioId ? handleRetry : undefined}
          onBack={handleBackToSelection}
        />
      )}
      
      {currentScreen === 'gameplay' && currentScenario && currentScenarioId && (
          <GameplayScreen
            scenario={currentScenario}
            scenarioId={currentScenarioId}
            missionId={currentMissionId ?? undefined}
            onBackToMenu={handleBackToSelection}
            onMissionChange={setCurrentMissionId}
          />
      )}
    </div>
  );
}

export default App;
